import { useEffect, useRef, useState } from "react";
import { createEntityStore } from "../../common/entityStore";
import { renderClose, useDialog } from "../../common/dialog";
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { propBoolField, propDateField, propEnumField, propTextField } from "../../common/entityStoreForm";
import { createListStore } from "../../common/listStore";
import { renderListStore } from "../../common/listStoreUi";
import { map } from "rxjs";
import { useLogEntrySeverities } from "../../common/enum";
import { downloadFromApi } from "../../common/download";
import { RunLogEntry, RunType, useRunTypes } from "./common";
import { RunInternalDataDialog } from "./RunInternalDataDialog";
import { createSignalRConnection } from "../../common/signalr";
import { delay } from "../../common/utils";
import { useCommStore } from "../../common/axios";

export const runHubs: { [runType in RunType]: string | null; } = {
    [RunType.MailOrder]: "mailOrderRun",
    [RunType.Starter]: "starterRun",
    [RunType.Shipment]: "shipmentRun",
    [RunType.Dunning]: "dunningRun",
    [RunType.ManualWriteOff]: "manualWriteOffRun",
    [RunType.OrderImport]: "orderImportRun",
    [RunType.Return]: "returnRun",
    [RunType.Payment]: "paymentRun",
    [RunType.WriteOff]: "writeOffRun",
    [RunType.RejectedImportCustomer]: null,
    [RunType.OrderCycleSkipImport]: "orderCycleSkipImport",
    [RunType.CustomerNoteImport]: "customerNoteImport",
    [RunType.CustomerReactivation]: "customerReactivationRun",
    [RunType.MailOrderCopy]: "mailOrderCopyRun",
    [RunType.OrderDelay]: "orderDelayRun",
    [RunType.MailOrderArticleReplace]: "mailOrderArticleReplaceRun",
    [RunType.ContOrderDeactivation]: "contOrderDeactivationRun",
    [RunType.Gdpr]: "gdprRun",
    [RunType.VatUpdate]: "vatUpdateRun",
    [RunType.EmailPreferences]: "ePreferenceRun",
    [RunType.OnlineReturnBatch]: "onlineReturnBatchRun"
};

export function RunDetailDialog(props: { id: number; close(): void; runType: RunType; }) {
    const [useStore] = useState(() => createEntityStore<Partial<RunDetailDto>>({
        path: "runs/runs", id: props.id, data: {}, onServerDataChanged(entity) {
            if (entity.isRunning) {
                watch(false);
            }
        },
    }));
    const [error, setError] = useState<string | null>(null);

    const runDisconnect = useRef<() => void>();
    useEffect(() => {
        return () => {
            runDisconnect.current?.();
        };
    }, []);

    async function watch(startIfNotRunning: boolean) {
        const customId = useStore.getState().serverData.customId;
        useCommStore.getState().requestStarted();
        while (true) {
            useStore.getState().setClientDataProp("isRunning", true);
            const connection = createSignalRConnection(runHubs[props.runType]!);
            let isStopped = false;
            try {
                await connection.start();
                setError(null);
                runDisconnect.current = () => {
                    isStopped = true;
                    connection.stop();
                };
                connection.on("log", (entry: RunLogEntry) => {
                    const store = useStore.getState();
                    store.setClientDataProp("logEntries", [entry].concat(store.clientData.logEntries || []));
                });
                connection.on("logs", (entries: RunLogEntry[]) => {
                    const maxId = Math.max(...entries.map(p => p.id).concat(0));
                    const store = useStore.getState();
                    store.setClientDataProp("logEntries", entries.filter(p => p.id > maxId)
                        .concat(entries.sort((a, b) => b.id - a.id)));
                });
                await connection.invoke(startIfNotRunning ? "restart" : "watch", customId);
                connection.stop();
                const store = useStore.getState();
                store.setClientDataProp("isRunning", false);
                store.fetchServerData(true);
                break;
            } catch (e) {
                if (isStopped) {
                    useStore.getState().setClientDataProp("isRunning", false);
                    break;
                } else {
                    setError(`Error: ${e}`);
                    connection.stop();
                    await delay(5000);
                }
            }
        }
        useCommStore.getState().requestFinished(undefined);
    }

    const [useLogEntryListStore] = useState(() => createListStore<RunLogEntry>({
        data: useStore.getState().clientData$.pipe(map(p => ({ items: p.data.logEntries || [], isFetching: p.isFetching }))),
        addIndexId: true, listProps: [
            { column: "id", label: "#", width: 70, type: "number" },
            { column: "time", label: "Time", width: 150, type: "datetime" },
            { column: "type", label: "Type", width: 100, enumStore: useLogEntrySeverities },
            { column: "message", label: "Message", flex: 1 },
        ]
    }));
    const { isRunning, isFinished, isFetching } = useStore(p => ({
        isRunning: p.clientData.isRunning,
        isFinished: p.clientData.finishedAt != null,
        isFetching: p.isFetching
    }));
    const internalDataDialog = useDialog(close => <RunInternalDataDialog runId={props.id} close={close} />);
    const { hasResultFile } = useStore(p => ({ hasResultFile: p.serverData.hasResultFile }))
    return <>
        {internalDataDialog.render()}
        <DialogTitle>{`Run ${props.id}${isRunning ? " (in progress)" : ""}`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={3}>{propEnumField(useStore)("runType", "Run type", useRunTypes, { disabled: true })}</Grid>
                <Grid item xs={2.5}>{propDateField(useStore)("createdAt", null, "Started at", { disabled: true, type: "datetime" })}</Grid>
                <Grid item xs={2.5}>{propDateField(useStore)("finishedAt", null, "Finished at", { disabled: true, type: "datetime" })}</Grid>
                <Grid item xs={2}>{propTextField(useStore)("userName", "User name", { disabled: true })}</Grid>
                <Grid item xs={2}>{propBoolField(useStore)("isFake", "Fake", { disabled: true })}</Grid>
                {error && <Grid item xs={12} mb={2}><Alert severity="error">{error}</Alert></Grid>}
            </Grid>
            {renderListStore(useLogEntryListStore)}
        </DialogContent>
        <DialogActions>
            <Button onClick={() => { runDisconnect.current?.(); watch(true); }} disabled={isRunning || isFinished || isFetching}>Restart</Button>
            <Button onClick={() => internalDataDialog.show()}>View data</Button>
            <Button onClick={() => downloadFromApi(`runs/runs/${props.id}/resultFile`)} disabled={!hasResultFile}>Download result file</Button>
            <Button onClick={() => downloadFromApi(`runs/runs/${props.id}/export`)}>Export logs</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>
}

export interface RunDetailDto {
    runType: RunType;
    createdAt: string;
    finishedAt: string | null;
    logEntries: RunLogEntry[];
    userName: string;
    isFake: boolean;
    hasResultFile: boolean;
    customId: string | null;
    isRunning: boolean;
}
